import type { PresaleMembership } from "../_api/use-presale-membership";
import type { PresaleCampaignSearch } from "./presale-funnel";
import type { PresaleCart } from "./presale-intent";
import type { PresaleOffer } from "./presale-offer";

/**
 * What a step can hand over.
 *
 * Every source is optional because the funnel knows different things at different steps:
 * steps 1/1b have the public plan read only, step 2 adds the resolved offer, step 3 has
 * the reservation itself. A step passes what it has and the missing properties come out
 * `undefined` — never `0`, which would read as "$0 cart" in the analytics rather than
 * "not known yet".
 */
export type PresaleAnalyticsSource = {
  search: PresaleCampaignSearch & { purchaseIntentId?: string };
  /** `GET /checkout-links/:id/plan` — the plan identity and its recurring price. */
  membership?: PresaleMembership | null;
  /** The campaign offer this funnel resolved to (steps 2 and 3). */
  offer?: PresaleOffer;
  /**
   * The reservation, priced by the platform (step 3 only).
   *
   * Wins over `offer` and `membership` on every key all three can answer: this is the row
   * `POST /checkout/pay` charges, and the other two are only what the URL proposed.
   */
  cart?: PresaleCart;
};

/**
 * Money in analytics is CENTS — the unit the `Join.*` events already send, so do NOT
 * convert.
 *
 * `Join.planValue` is `stripePrice.unit_amount`
 * (`platform/src/resources/checkout-links/checkout-link-plan-price.service.ts`), and
 * `Join.subTotal` / `.total` / `.nextPayment` come from `PriceService`, whose labels are
 * built by `formatCurrencyCentsLabel` — i.e. by dividing those very fields by 100
 * (`platform/src/lib/money/currency-label.mapper.ts`). Emitting presale in dollars would put the two
 * funnels 100x apart in the one query these shared key names exist to serve.
 */
const asMoney = (cents: number | null | undefined): number | undefined =>
  cents ?? undefined;

/**
 * The platform's own formatted labels, `null`-coerced.
 *
 * Presale used to re-format cents here, pinning two fraction digits so the strings matched
 * `formatCurrencyCentsLabel` character for character. Reading the labels the platform
 * already sent removes that obligation — a breakdown on `totalFormatted` cannot split one
 * amount across two buckets when both funnels report the same string.
 */
const asLabel = (label: string | null | undefined): string | undefined =>
  label ?? undefined;

/**
 * The presale funnel's analytics payload, under the JOIN funnel's property names.
 *
 * One builder rather than a literal per call site, for the reason
 * `getInviteAnalyticsProps` exists: fourteen keys restated at twelve call sites is
 * thirteen chances for one of them to drift and silently fall out of the shared query.
 *
 * `stepId` / `stepName` are deliberately NOT here — they are per-event, so every call site
 * spreads this and then adds its own, exactly as the join call sites do.
 */
export function getPresaleAnalyticsProps(source: PresaleAnalyticsSource) {
  const { search, membership, offer, cart } = source;
  const priceDetail = cart?.priceDetail;

  return {
    flowStage: "presale" as const,

    // `Plan.key`. Empty on a visit that carries no `checkoutLinkId` — the plan read is
    // gated on it, and `/checkout-links/list` (which would name the tier) is auth-gated,
    // so a signed-out step 1 with no link genuinely has no plan identity to report.
    planKey: cart?.planKey || membership?.planKey || "",

    // `planValue` is the PLAN's recurring unit price, matching `Join.planValue` — not the
    // cart total. Both sources are the campaign link's price override for that plan.
    planValue: asMoney(
      cart?.membership?.price?.unitAmount ?? membership?.unitAmountCents,
    ),
    planValueFormatted: asLabel(
      cart?.membership?.price?.formattedPrice ?? membership?.formattedPrice,
    ),

    // Pre-discount sum of every cart line — the same thing `Join.subTotal` is, and now
    // literally the same field.
    subTotal: asMoney(priceDetail?.subTotal),
    subTotalFormatted: asLabel(priceDetail?.subTotalLabel),

    // What is charged NOW. Deliberately `dueToday`, not `priceDetail.total`: `total` is
    // the post-discount subtotal BEFORE the convenience fee, whereas `dueToday` is the
    // amount `POST /checkout/pay` collects. Join sends `total` here; matching it would
    // silently redefine a live presale metric, so this key keeps its own meaning.
    total: asMoney(priceDetail?.dueToday),
    totalFormatted: asLabel(priceDetail?.dueTodayLabel),

    // The recurring amount due at the next cycle — $0 during the presale, by design.
    nextPayment: asMoney(priceDetail?.nextPayment),
    nextPaymentFormatted: asLabel(priceDetail?.nextPaymentLabel),

    // `CheckoutLink.id`. The link the reservation is BOUND to wins over the URL: it is
    // what step 2 wrote onto the purchase intent and what the charge reads, so a stale or
    // unresolvable link in the URL must not be the one attributed. Steps without a
    // reservation fall back to the resolved offer, then to the URL.
    checkoutId:
      cart?.checkoutLinkId ||
      offer?.checkoutLinkId ||
      search.checkoutLinkId ||
      "",

    // The code recorded on the reservation, then the one that actually produced a
    // discount, then the URL's — so a valid code worth $0 and a code that failed
    // validation are both still attributable.
    discountCode:
      cart?.discountCode ||
      priceDetail?.discount?.code ||
      search.discountCode ||
      "",

    // Everything in the cart that is not the membership — the presale twin of Join's
    // `extraAddOnKeys` (`home-evaluation`, `service-credit`).
    addOns:
      cart?.addOnKeys ??
      (offer?.lines ?? [])
        .filter((line) => !line.isMembership)
        .map((line) => line.key),

    referId: search.referId || "",
    referredBy: search.referredBy || "",

    // Presale-only extra: no `Join.*` counterpart to collide with, and the only key that
    // joins an analytics event back to the row `POST /checkout/pay` charged.
    purchaseIntentId: search.purchaseIntentId || "",
  };
}

export type PresaleAnalyticsProps = ReturnType<typeof getPresaleAnalyticsProps>;
